<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  isChecked?: boolean
  checkedIcon?: string
  uncheckedIcon?: string
}
const props = withDefaults(defineProps<Props>(), {
  isChecked: false,
})

interface Emits {
  (e: 'update:isChecked', value: boolean): void
}
const emit = defineEmits<Emits>()

const checkedIconClass = computed(() => {
  return props.checkedIcon ? props.checkedIcon : 'mi-check'
})

const uncheckedIconClass = computed(() => {
  return props.uncheckedIcon ? props.uncheckedIcon : 'mi-close'
})
</script>

<template>
  <div
    class="toggle"
    :class="{ 'is-checked': props.isChecked }"
    @click="emit('update:isChecked', !props.isChecked)"
  >
    <div class="dot">
      <transition name="fade" mode="out-in" :duration="{ enter: 100, leave: 50 }">
        <i
          v-if="props.isChecked"
          class="icon true-icon checked mi-outlined"
          :class="checkedIconClass"
        />
        <i v-else class="icon false-icon unchecked mi-outlined" :class="uncheckedIconClass" />
      </transition>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'frontend_stylesheets/theme-variables' as themeVariables;

.toggle {
  width: 52px;
  height: 32px;
  border-radius: 100px;
  position: relative;
  cursor: pointer;
  transition: all 0.1s cubic-bezier(0.55, 0, 0.1, 1);
  background: #e6e6e61f;
  border: 2px solid #17191a1f;
  box-sizing: border-box;

  &:hover {
    border-color: #005291;
  }

  &.is-checked {
    background: map.get(themeVariables.$colors, primary);
    border-color: transparent;
  }
}

.dot {
  height: calc(100% - 4px);
  aspect-ratio: 1/1;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: 2px;
  border: 2px solid #a5a5a6;
  transition: transform 0.15s cubic-bezier(0.55, 0, 0.1, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #a5a5a6;

  .toggle:hover & {
    background: map.get(themeVariables.$colors, primary);
    border-color: transparent;
    color: white;
  }
  .toggle.is-checked & {
    background: hsl(208deg, 57%, 95%);
    transform: translateX(calc(100% - 4px));
    border-color: transparent;
    color: #001e3d;

    .erebus-app[data-theme='dark'] & {
      background: #032b54;
      color: map.get(themeVariables.$colors, primary);
    }
  }
}

.icon {
  position: absolute;
  top: 2px;
}
</style>
